import { ChangeDetectionStrategy, Component, computed, ElementRef, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TableOfContentsComponent, TocItem } from '@examdojo/core/ui/table-of-contents';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, filter, switchMap, tap, throttleTime } from 'rxjs/operators';
import { ContentBlock } from '../../model';
import { extractHeadingsFromBlocks, TOC_ID_DATA_ATTR } from './extract-headings.util';

const SMOOTH_SCROLL_DURATION_MS = 600;

@Component({
  selector: 'dojo-content-block-toc',
  templateUrl: './content-block-toc.component.html',
  styleUrl: './content-block-toc.component.scss',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableOfContentsComponent],
})
export class ContentBlockTocComponent {
  constructor(private readonly elementRef: ElementRef) {
    merge(
      this.setInitialActiveHeading(),
      this.updateActiveHeadingOnScroll(),
      this.updateActiveHeadingOnWindowScroll(),
      this.autoScrollToActiveItem(),
      this.resumeAutoScrollAfterPause(),
    )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  readonly blocks = input.required<ContentBlock[]>();
  readonly scrollableParent = input<HTMLElement>();
  readonly queryRoot = input<HTMLElement>();

  private readonly headings = computed(() => extractHeadingsFromBlocks(this.blocks()));
  private readonly activeAnchorIds = signal<ReadonlySet<string>>(new Set());
  private isAutoScrollPaused = false;
  private readonly pauseAutoScroll$ = new Subject<void>();

  readonly tocItems = computed<TocItem[]>(() => {
    const activeIds = this.activeAnchorIds();
    return this.headings().map((item) => ({
      id: item.id,
      level: item.level,
      textContent: item.textContent,
      isActive: activeIds.has(item.id),
    }));
  });

  onItemClick(id: string): void {
    const scrollableParent = this.scrollableParent();
    if (!scrollableParent) {
      return;
    }

    const element = this.findHeadingElement(id);
    if (!element) {
      return;
    }

    this.isAutoScrollPaused = true;
    this.pauseAutoScroll$.next();

    const scrollableParentRect = scrollableParent.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const scrollOffset = 16;
    const targetPosition = elementRect.top - scrollableParentRect.top + scrollableParent.scrollTop - scrollOffset;

    scrollableParent.scrollTo({ top: targetPosition, behavior: 'smooth' });
  }

  private resumeAutoScrollAfterPause() {
    return this.pauseAutoScroll$.pipe(
      switchMap(() =>
        new Promise<void>((resolve) => setTimeout(resolve, SMOOTH_SCROLL_DURATION_MS)),
      ),
      tap(() => (this.isAutoScrollPaused = false)),
    );
  }

  private setInitialActiveHeading() {
    return toObservable(this.headings).pipe(
      filter((items) => items.length > 0),
      debounceTime(0),
      tap(() => this.updateActiveHeadings()),
    );
  }

  private updateActiveHeadingOnScroll() {
    return toObservable(this.scrollableParent).pipe(
      filter(Boolean),
      switchMap((parent) =>
        fromEvent(parent, 'scroll').pipe(
          throttleTime(80, undefined, { leading: true, trailing: true }),
          tap(() => this.updateActiveHeadings()),
        ),
      ),
    );
  }

  private updateActiveHeadingOnWindowScroll() {
    return fromEvent(window, 'scroll').pipe(
      throttleTime(80, undefined, { leading: true, trailing: true }),
      tap(() => this.updateActiveHeadings()),
    );
  }

  private updateActiveHeadings(): void {
    const items = this.headings();
    if (!items.length) {
      return;
    }

    const scrollableParent = this.scrollableParent();
    const parentRect = scrollableParent?.getBoundingClientRect();
    const viewportTop = parentRect?.top ?? 0;
    const viewportBottom = parentRect?.bottom ?? window.innerHeight;
    const activeIds = new Set<string>();

    const positions: Array<{ id: string; top: number }> = [];
    for (const item of items) {
      const element = this.findHeadingElement(item.id);
      if (!element) {
        continue;
      }
      positions.push({ id: item.id, top: element.getBoundingClientRect().top });
    }

    const activeThreshold = 60;

    for (let i = 0; i < positions.length; i++) {
      const sectionTop = positions[i].top;
      const sectionBottom = i < positions.length - 1 ? positions[i + 1].top : Number.POSITIVE_INFINITY;

      const isVisible = sectionTop < viewportBottom && sectionBottom > viewportTop + activeThreshold;
      if (isVisible) {
        activeIds.add(positions[i].id);
      }
    }

    if (activeIds.size === 0 && items.length > 0) {
      activeIds.add(items[0].id);
    }

    this.activeAnchorIds.set(activeIds);
  }

  private autoScrollToActiveItem() {
    return toObservable(this.activeAnchorIds).pipe(
      filter((ids) => ids.size > 0),
      filter(() => !this.isAutoScrollPaused),
      debounceTime(0),
      tap((ids) => {
        const lastActiveId = [...ids][ids.size - 1];
        const hostEl = this.elementRef.nativeElement as HTMLElement;
        const activeEl = hostEl.querySelector(`a[data-toc-item-id="${lastActiveId}"]`) as HTMLElement | null;
        if (activeEl) {
          this.scrollIntoNearestParent(activeEl);
        }
      }),
    );
  }

  private scrollIntoNearestParent(element: HTMLElement): void {
    const scrollParent = this.findScrollableParent(element);
    if (!scrollParent) {
      return;
    }

    const parentRect = scrollParent.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (elementRect.top < parentRect.top) {
      scrollParent.scrollTo({ top: scrollParent.scrollTop + elementRect.top - parentRect.top, behavior: 'smooth' });
    } else if (elementRect.bottom > parentRect.bottom) {
      scrollParent.scrollTo({ top: scrollParent.scrollTop + elementRect.bottom - parentRect.bottom, behavior: 'smooth' });
    }
  }

  private findScrollableParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
      const { overflowY } = getComputedStyle(parent);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private findHeadingElement(id: string): Element | null {
    const queryRoot = this.queryRoot() ?? this.scrollableParent();
    if (!queryRoot) {
      return null;
    }
    return queryRoot.querySelector(`[${TOC_ID_DATA_ATTR}="${id}"]`);
  }
}
